import React from 'react';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import LanguageSwitcher from '@/components/LanguageSwitcher';
import { useLanguage } from '@/contexts/LanguageContext';
import { Card } from '@/components/ui/card';
import { Book, FileText, Home, Users, Briefcase, Heart } from 'lucide-react';

const LegalGuides = () => {
  const { language } = useLanguage();

  const guides = language === 'ur' ? [
    {
      icon: <FileText className="w-8 h-8 text-blue-600" />,
      title: 'عدالت میں درخواست کیسے دائر کریں',
      content: 'پاکستان میں عدالت میں درخواست دائر کرنے کے لیے آپ کو مناسب فارمیٹ میں درخواست تیار کرنی ہوگی۔ درخواست میں آپ کا نام، پتہ، مدعا علیہ کی تفصیلات، اور آپ کی شکایت کا واضح بیان ہونا ضروری ہے۔ عدالت بوٹ آپ کی مدد کر سکتا ہے یہ دستاویز تیار کرنے میں۔'
    },
    {
      icon: <Home className="w-8 h-8 text-blue-600" />,
      title: 'جائیداد کے تنازعات',
      content: 'جائیداد کے تنازعات پاکستان میں عام ہیں۔ اگر آپ کو جائیداد پر قبضہ کا مسئلہ ہے، تو آپ کو سول کورٹ میں درخواست دائر کرنی ہوگی۔ ضروری دستاویزات میں ملکیت کے کاغذات، قبضے کا ثبوت، اور گواہوں کے بیانات شامل ہیں۔'
    },
    {
      icon: <Heart className="w-8 h-8 text-blue-600" />,
      title: 'خاندانی قوانین',
      content: 'خاندانی قوانین میں شادی، طلاق، نان نفقہ، اور بچوں کی حضانت شامل ہیں۔ پاکستان میں خاندانی قوانین مسلم فیملی لاز آرڈیننس 1961 کے تحت آتے ہیں۔ طلاق کی صورت میں، یونین کونسل کو نوٹس دینا لازمی ہے۔'
    },
    {
      icon: <Users className="w-8 h-8 text-blue-600" />,
      title: 'وراثت کے قوانین',
      content: 'اسلامی قانون کے مطابق، وراثت کی تقسیم مقررہ حصوں کے مطابق ہوتی ہے۔ بیٹوں اور بیٹیوں، بیوی، والدین سب کے حصے مقرر ہیں۔ وراثت کے تنازعات کی صورت میں عدالت سے رجوع کیا جا سکتا ہے۔'
    },
    {
      icon: <Briefcase className="w-8 h-8 text-blue-600" />,
      title: 'ملازمت کے قوانین',
      content: 'پاکستان میں ملازمین کے حقوق محفوظ ہیں۔ اگر آپ کو غیر قانونی طور پر نکالا گیا ہے، تو آپ لیبر کورٹ میں کیس کر سکتے ہیں۔ ملازمین کو کم از کم اجرت، چھٹیوں، اور محفوظ کام کی جگہ کا حق ہے۔'
    },
    {
      icon: <Book className="w-8 h-8 text-blue-600" />,
      title: 'معاہدے کی قانونی حیثیت',
      content: 'ایک قانونی معاہدہ دو یا زیادہ افراد کے درمیان لکھا ہوا یا زبانی agreement ہے۔ تحریری معاہدے زیادہ مضبوط ہوتے ہیں۔ معاہدے میں فریقین کے نام، شرائط، رقم، اور دستخط ضروری ہیں۔'
    }
  ] : [
    {
      icon: <FileText className="w-8 h-8 text-blue-600" />,
      title: 'How to File an Application in Court',
      content: 'To file an application in court in Pakistan, you need to prepare it in the proper format. The application must include your name, address, defendant details, and a clear statement of your complaint. Adalat Bot can help you prepare this document.'
    },
    {
      icon: <Home className="w-8 h-8 text-blue-600" />,
      title: 'Property Disputes',
      content: 'Property disputes are common in Pakistan. If you have a property possession issue, you need to file an application in civil court. Necessary documents include ownership papers, proof of possession, and witness statements.'
    },
    {
      icon: <Heart className="w-8 h-8 text-blue-600" />,
      title: 'Family Laws',
      content: 'Family laws include marriage, divorce, maintenance, and child custody. In Pakistan, family laws fall under the Muslim Family Laws Ordinance 1961. In case of divorce, it is mandatory to give notice to the Union Council.'
    },
    {
      icon: <Users className="w-8 h-8 text-blue-600" />,
      title: 'Inheritance Laws',
      content: 'According to Islamic law, inheritance is distributed according to fixed shares. Sons and daughters, wife, parents all have fixed shares. In case of inheritance disputes, court can be approached.'
    },
    {
      icon: <Briefcase className="w-8 h-8 text-blue-600" />,
      title: 'Employment Laws',
      content: 'Employee rights are protected in Pakistan. If you have been illegally terminated, you can file a case in labor court. Employees have the right to minimum wage, leave, and a safe workplace.'
    },
    {
      icon: <Book className="w-8 h-8 text-blue-600" />,
      title: 'Legal Status of Contracts',
      content: 'A legal contract is a written or verbal agreement between two or more parties. Written contracts are stronger. A contract must include party names, terms, amount, and signatures.'
    }
  ];

  return (
    <div className={`min-h-screen bg-adalat-gradient ${language === 'ur' ? 'font-urdu' : 'font-english'}`}>
      <div className="container mx-auto py-8 px-4">
        <div className="flex justify-between items-center mb-8">
          <Header />
          <LanguageSwitcher />
        </div>

        <main className={`max-w-6xl mx-auto ${language === 'ur' ? 'rtl' : 'ltr'}`}>
          <Card className="p-8 mb-8">
            <h1 className={`${language === 'ur' ? 'text-urdu-3xl text-right' : 'text-4xl'} font-bold mb-4 text-blue-700`}>
              {language === 'ur' ? 'قانونی رہنما' : 'Legal Guides'}
            </h1>
            <p className={`${language === 'ur' ? 'text-urdu-lg text-right' : 'text-lg'} text-gray-600 mb-8`}>
              {language === 'ur'
                ? 'پاکستان میں عام قانونی معاملات کے بارے میں معلومات'
                : 'Information about common legal matters in Pakistan'}
            </p>

            <div className="grid md:grid-cols-2 gap-6">
              {guides.map((guide, index) => (
                <Card key={index} className="p-6 hover:shadow-lg transition-shadow">
                  <div className={`flex ${language === 'ur' ? 'flex-row-reverse' : 'flex-row'} items-start gap-4`}>
                    <div className="flex-shrink-0">
                      {guide.icon}
                    </div>
                    <div className="flex-1">
                      <h3 className={`${language === 'ur' ? 'text-urdu-xl text-right' : 'text-xl'} font-semibold mb-3 text-blue-700`}>
                        {guide.title}
                      </h3>
                      <p className={`${language === 'ur' ? 'text-urdu-base text-right' : 'text-base'} text-gray-700 leading-relaxed`}>
                        {guide.content}
                      </p>
                    </div>
                  </div>
                </Card>
              ))}
            </div>

            <div className="mt-8 p-6 bg-blue-50 rounded-lg">
              <h2 className={`${language === 'ur' ? 'text-urdu-2xl text-right' : 'text-2xl'} font-bold mb-4 text-blue-700`}>
                {language === 'ur' ? 'مزید معلومات کی ضرورت ہے؟' : 'Need More Information?'}
              </h2>
              <p className={`${language === 'ur' ? 'text-urdu-lg text-right' : 'text-lg'} text-gray-700`}>
                {language === 'ur'
                  ? 'ہمارے چیٹ بوٹ سے کوئی بھی قانونی سوال پوچھیں یا عدالت بوٹ استعمال کرکے اپنی دستاویز تیار کریں۔ ہماری AI ٹیکنالوجی آپ کی مدد کے لیے ہمیشہ تیار ہے۔'
                  : 'Ask any legal question from our chatbot or use Adalat Bot to prepare your document. Our AI technology is always ready to help you.'}
              </p>
            </div>
          </Card>
        </main>

        <Footer />
      </div>
    </div>
  );
};

export default LegalGuides;